import type { RoomLike, RemoteParticipantLike } from '@questvisionstream/client';
import { DEMO_SCENARIO } from './demoFixtures';

/**
 * An in-memory LiveKit room for `?demo=` runs: connects without a server,
 * seats a vision bot and one assistant, plays the server-authority training
 * flow (reacting to the client's begin/action/sync messages the way
 * training_flow.py does), streams detections with a TV box, and draws one
 * assistant stroke around it. Every message uses the deployed wire shapes,
 * so the whole client runs unchanged - only the transport is fake.
 *
 * This is the zero-spend way to iterate on the UX and to capture the
 * documentation screenshots. Never wired in a production path.
 */

type Listener = (...args: unknown[]) => void;

interface DemoParticipant extends RemoteParticipantLike {
  readonly identity: string;
  readonly name: string;
  readonly metadata: string;
}

const encode = (value: unknown): Uint8Array => new TextEncoder().encode(JSON.stringify(value));

export interface DemoRoomOptions {
  /** Press the wearer's form options automatically (2.5 s after each step), so the flow walks itself. */
  readonly autoplay?: boolean;
}

export class DemoRoom implements RoomLike {
  readonly name = 'qvs-main';
  private readonly options: DemoRoomOptions;

  constructor(options: DemoRoomOptions = {}) {
    this.options = options;
  }
  readonly remoteParticipants = new Map<string, DemoParticipant>();
  readonly localParticipant = {
    identity: 'wearer',
    publishTrack: async () => ({ mute: async () => undefined, unmute: async () => undefined }),
    publishData: async (data: Uint8Array, options?: Record<string, unknown>) => {
      this.onLocalData(new TextDecoder().decode(data), String(options?.['topic'] ?? ''));
    },
  };

  private readonly listeners = new Map<string, Set<Listener>>();
  private readonly timers: Array<ReturnType<typeof setTimeout>> = [];
  private stepIndex = -1;
  private status: 'idle' | 'running' | 'completed' = 'idle';
  private detected = new Map<string, string>();
  private frame = 0;
  private detectionsTimer: ReturnType<typeof setInterval> | undefined;
  private strokeDrawn = false;

  on(event: string, listener: (...args: never[]) => void): unknown {
    const set = this.listeners.get(event) ?? new Set();
    set.add(listener as Listener);
    this.listeners.set(event, set);
    return this;
  }

  private emit(event: string, ...args: unknown[]): void {
    this.listeners.get(event)?.forEach((listener) => listener(...args));
  }

  async connect(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.later(300, () => this.seatParticipant('vision', 'vision', '{"role":"vision"}'));
    this.later(600, () => {
      this.seatParticipant('assistant:jane#4f2a', 'Jane', '');
      this.publish('presence', { type: 'presence', id: 'assistant:jane#4f2a', name: 'Jane', role: 'assistant', observer: false, av: { camera: false, mic: true } }, 'assistant:jane#4f2a');
      this.emit('trackPublished', { kind: 'audio', isMuted: false }, this.remoteParticipants.get('assistant:jane#4f2a'));
    });
    this.later(900, () => {
      this.publish('detections', { type: 'ready' }, 'vision');
      this.emitTraining('loaded', { scenario: DEMO_SCENARIO });
      this.detectionsTimer = setInterval(() => this.tickDetections(), 500);
    });
  }

  async disconnect(): Promise<void> {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.length = 0;
    if (this.detectionsTimer) clearInterval(this.detectionsTimer);
    this.emit('disconnected');
  }

  // ------------------------------------------------------------ the "server"

  private onLocalData(json: string, topic: string): void {
    if (topic !== 'training') return;
    let message: { type?: string; command?: string; stepIndex?: number } = {};
    try {
      message = JSON.parse(json) as typeof message;
    } catch {
      return;
    }
    if (message.type === 'trainingControl') {
      if (message.command === 'begin') this.activate(0, '', '', 'detection');
      else if (message.command === 'reset') {
        this.status = 'idle';
        this.stepIndex = -1;
        this.detected.clear();
        this.emitTraining('state', { scenario: DEMO_SCENARIO });
      } else if (message.command === 'sync') this.emitTraining('state', { scenario: DEMO_SCENARIO });
    } else if (message.type === 'trainingAction' && message.stepIndex === this.stepIndex) {
      // A form action advances the queue, like the machine's complete_step.
      const step = DEMO_SCENARIO.steps[this.stepIndex]!;
      const arrivedClass = step.result || step.waitingClass;
      const arrivedState = step.resultState || 'present';
      if (arrivedClass) this.detected.set(arrivedClass, arrivedState);
      if (this.stepIndex + 1 >= DEMO_SCENARIO.steps.length) {
        this.status = 'completed';
        this.stepIndex = -1;
        this.emitTraining('completed', { arrivedClass, arrivedState, source: 'action' });
      } else {
        this.activate(this.stepIndex + 1, arrivedClass, arrivedState, 'action');
      }
    }
  }

  private activate(index: number, arrivedClass: string, arrivedState: string, source: string): void {
    this.status = 'running';
    this.stepIndex = index;
    this.emitTraining('step', { stepCount: DEMO_SCENARIO.steps.length, arrivedClass, arrivedState, source });

    // Autoplay: a ghost wearer presses the primary option a moment later,
    // except on detection-gated steps (the TV sighting advances those) and
    // the final step, where the flow parks so the full scene stays on view.
    const step = DEMO_SCENARIO.steps[index];
    const isFinal = step ? step.result === '' && step.resultState === '' : true;
    if (this.options.autoplay && step && step.options.length > 0 && step.result !== 'tv' && !isFinal) {
      this.later(2500, () => {
        if (this.stepIndex === index) this.onLocalData(JSON.stringify({ type: 'trainingAction', stepIndex: index }), 'training');
      });
    }
  }

  private emitTraining(event: string, fields: Record<string, unknown>): void {
    const step = DEMO_SCENARIO.steps[this.stepIndex];
    this.publish(
      'training',
      {
        type: 'training',
        event,
        ...fields,
        state: {
          status: this.status,
          stepIndex: this.stepIndex,
          expectedClass: step ? step.result || step.waitingClass : '',
          expectedState: step ? step.resultState || 'present' : '',
          discarded: 0,
          detectedObjects: Object.fromEntries(this.detected),
        },
      },
      'vision',
    );
  }

  /** A TV in the middle of the frame once the flow is looking for one; a stroke around it once found. */
  private tickDetections(): void {
    this.frame += 1;
    const showTv = this.status === 'running' && this.stepIndex >= 1;
    const box = [200, 150, 440, 330];
    this.publish(
      'detections',
      {
        type: 'detections',
        frame: this.frame,
        pts: this.frame * 3000,
        ptsClockHz: 90_000,
        width: 640,
        height: 480,
        detections: showTv ? [{ label: 'tv', conf: 0.91, bbox: box }] : [],
      },
      'vision',
    );

    // The server advances "Look for a Monitor" itself when the tv is sighted.
    if (this.status === 'running' && this.stepIndex === 1 && this.frame % 4 === 0) {
      this.detected.set('tv', 'present');
      this.activate(2, 'tv', 'present', 'detection');
    }

    if (showTv && this.stepIndex >= 2 && !this.strokeDrawn) {
      this.strokeDrawn = true;
      this.later(800, () => this.drawCircle(this.frame * 3000));
    }
  }

  private drawCircle(ptsRef: number): void {
    const points: Array<{ x: number; y: number }> = [];
    for (let i = 0; i <= 24; i += 1) {
      const a = (i / 24) * Math.PI * 2;
      points.push({ x: 0.5 + 0.22 * Math.cos(a), y: 0.5 + 0.24 * Math.sin(a) });
    }
    const sender = 'assistant:jane#4f2a';
    this.publish('strokes', { type: 'stroke', phase: 'begin', strokeId: 'demo-1', sender, ptsRef, color: 'auto', width: 0.008, points: points.slice(0, 8) }, sender);
    this.later(120, () => this.publish('strokes', { type: 'stroke', phase: 'append', strokeId: 'demo-1', seq: 1, points: points.slice(8, 16) }, sender));
    this.later(240, () => this.publish('strokes', { type: 'stroke', phase: 'end', strokeId: 'demo-1', ptsRef, points }, sender));
  }

  // ---------------------------------------------------------------- plumbing

  private seatParticipant(identity: string, name: string, metadata: string): void {
    const participant: DemoParticipant = { identity, name, metadata, getTrackPublications: () => [] };
    this.remoteParticipants.set(identity, participant);
    this.emit('participantConnected', participant);
  }

  private publish(topic: string, payload: unknown, sender: string): void {
    this.emit('dataReceived', encode(payload), this.remoteParticipants.get(sender) ?? { identity: sender }, undefined, topic);
  }

  private later(ms: number, fn: () => void): void {
    this.timers.push(setTimeout(fn, ms));
  }
}

export function createDemoRoomFactory(options: DemoRoomOptions = {}): () => RoomLike {
  return () => new DemoRoom(options);
}

/**
 * A synthetic "passthrough" feed for desktop demo runs: a canvas that paints a
 * moving gradient and a caption, captured as a MediaStream so the publish
 * gate and the room service see a live video track.
 */
export function syntheticCameraStream(): MediaStream {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 480;
  const ctx = canvas.getContext('2d')!;
  let t = 0;
  const paint = (): void => {
    t += 1;
    const gradient = ctx.createLinearGradient(0, 0, 640, 480);
    gradient.addColorStop(0, `hsl(${(t * 0.5) % 360}, 30%, 22%)`);
    gradient.addColorStop(1, `hsl(${(t * 0.5 + 120) % 360}, 30%, 12%)`);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 640, 480);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '24px sans-serif';
    ctx.fillText('demo camera (synthetic)', 24, 48);
    requestAnimationFrame(paint);
  };
  paint();
  return canvas.captureStream(30);
}
